/**
 * CreditReceiveModal — مودال دریافتی‌های منبع اعتباری
 * جدول: JamBudget.dbo.tblCreditReceive
 * فهرست، ثبت، ویرایش و حذف
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ui } from '../../components/ListPage';
import { jsonOf, netErr } from '../../utils/http';
import { ExcelModalButton } from '../../components/ExcelExportModal';

const toFarsi = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt  = n => (Number(n||0) === 0) ? '۰' : Number(n).toLocaleString('fa-IR');

const API = (path, opts={}) => fetch(`/api/accounting${path}`, {
  headers: { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = { letter_no:'', letter_date:'', receive_cash:0, receive_doc:0, receive_fund:0, remark:'' };

const EXCEL_COLS = [
  { key: 'letter_no',    label: 'شماره نامه' },
  { key: 'letter_date',  label: 'تاریخ نامه' },
  { key: 'receive_cash', label: 'دریافتی نقدی', num: true },
  { key: 'receive_doc',  label: 'اسناد خزانه',  num: true },
  { key: 'receive_fund', label: 'تنخواه',       num: true },
  { key: 'remark',       label: 'توضیح' },
];

export default function CreditReceiveModal({ creditId, year, onClose, onChanged }) {
  const [rows, setRows]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode]       = useState(null); // null | 'new' | 'edit' | 'delete'
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [creditDisplay, setCreditDisplay] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const j = await API(`/credits/${creditId}/receives/?year=${year}`);
    setLoading(false);
    if (j.ok) { setRows(j.data); setCreditDisplay(j.credit_display || ''); }
    else setError(j.msg);
  }, [creditId, year]);

  useEffect(() => { load(); }, [load]);

  const openNew    = () => { setForm({...EMPTY}); setError(''); setMode('new'); };
  const openEdit   = () => { if (!selected) return; setForm({...selected}); setError(''); setMode('edit'); };
  const openDelete = () => { if (!selected) return; setMode('delete'); };

  const save = async () => {
    const cash = Number(form.receive_cash||0), doc = Number(form.receive_doc||0), fund = Number(form.receive_fund||0);
    if (cash === 0 && doc === 0 && fund === 0) { setError('حداقل یکی از مبالغ دریافتی باید مقدار داشته باشد'); return; }
    setSaving(true); setError('');
    let j;
    if (mode === 'new') {
      j = await API(`/credits/${creditId}/receives/create/`, {
        method:'POST', body: JSON.stringify({ ...form, year, financial_year: year }),
      });
    } else {
      j = await API(`/credit-receives/${selected.id}/update/`, {
        method:'PUT', body: JSON.stringify(form),
      });
    }
    setSaving(false);
    if (j.ok) { setMode(null); setSelected(null); load(); if (onChanged) onChanged(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/credit-receives/${selected.id}/delete/`, { method:'DELETE' });
    setSaving(false);
    if (j.ok) { setMode(null); setSelected(null); load(); if (onChanged) onChanged(); }
    else setError(j.msg);
  };

  const F = (label, name, type='text') => (
    <div style={{display:'flex', flexDirection:'column', gap:'4px', flex:1}}>
      <label style={ui.mlabel}>{label}</label>
      <input type={type} value={form[name]||''} onChange={e => setForm(f=>({...f,[name]: type==='number' ? Number(e.target.value) : e.target.value}))} style={ui.input} dir={type==='number'?'ltr':'rtl'} />
    </div>
  );

  return (
    <div style={ui.overlay} onClick={onClose}>
      <div style={{...ui.modal, maxWidth:'780px', maxHeight:'90vh', display:'flex', flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
        {/* هدر */}
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>دریافتی منبع اعتباری {creditDisplay}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>

        {/* تولبار */}
        <div style={{padding:'10px 18px', background:'#f5f7fa', borderBottom:'1px solid #e8eaf6', display:'flex', gap:'8px', alignItems:'center'}}>
          <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>
          <button onClick={openEdit} disabled={!selected} style={{...ui.btnEdit, opacity:selected?1:0.4}}>✎ ویرایش</button>
          <button onClick={openDelete} disabled={!selected} style={{...ui.btnDel, opacity:selected?1:0.4}}>✕ حذف</button>
          <ExcelModalButton
            title={`دریافتی منبع اعتباری ${creditDisplay}`}
            columns={EXCEL_COLS}
            rows={rows}
            style={{ padding:'4px 10px', fontSize:'11px' }}
          />
          {loading && <span>⏳</span>}
          <span style={{marginRight:'auto', fontSize:'12px', color:'#888'}}>{toFarsi(rows.length)} ردیف</span>
        </div>

        {/* جدول */}
        <div style={{flex:1, overflowY:'auto', padding:'0'}}>
          <table style={{...ui.table, tableLayout:'fixed', width:'100%'}}>
            <thead style={{position:'sticky', top:0, zIndex:5}}>
              <tr>
                <th style={{...th, width:60}}>ردیف</th>
                <th style={{...th, width:100}}>شماره نامه</th>
                <th style={{...th, width:100}}>تاریخ نامه</th>
                <th style={{...th, width:120}}>دریافتی نقدی</th>
                <th style={{...th, width:120}}>اسناد خزانه</th>
                <th style={{...th, width:120}}>تنخواه</th>
                <th style={{...th, width:180}}>توضیح</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const sel = selected?.id === r.id;
                return (
                  <tr key={r.id} style={{background: sel ? '#1a2f50' : i%2===0 ? '#fff' : '#fdf6e3', cursor:'pointer'}}
                    onClick={()=>setSelected(sel?null:r)}
                    onDoubleClick={()=>{ setSelected(r); setForm({...r}); setError(''); setMode('edit'); }}>
                    <td style={{...td, color:sel?'#C8A84B':'#888', textAlign:'center'}}>{toFarsi(i+1)}</td>
                    <td style={{...td, color:sel?'#fff':'#333'}}>{r.letter_no}</td>
                    <td style={{...td, color:sel?'#fff':'#333', direction:'ltr', textAlign:'center'}}>{r.letter_date}</td>
                    <td style={{...td, ...numTd, color:sel?'#81c784':'#1b5e20'}}>{numFmt(r.receive_cash)}</td>
                    <td style={{...td, ...numTd, color:sel?'#90caf9':'#0d47a1'}}>{numFmt(r.receive_doc)}</td>
                    <td style={{...td, ...numTd, color:sel?'#ce93d8':'#6a1b9a'}}>{numFmt(r.receive_fund)}</td>
                    <td style={{...td, color:sel?'#fff':'#555', textAlign:'right'}}>{r.remark}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} style={{padding:'30px', textAlign:'center', color:'#999'}}>هنوز دریافتی ثبت نشده</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* فرم ثبت/ویرایش */}
        {(mode === 'new' || mode === 'edit') && (
          <div style={{borderTop:'2px solid #1a2f50', padding:'16px 18px', background:'#f8f9fc'}}>
            <div style={{fontWeight:'700', color:'#1a2f50', marginBottom:'10px', fontSize:'13px'}}>
              {mode === 'new' ? '＋ ثبت دریافتی جدید' : '✎ ویرایش دریافتی'}
            </div>
            {error && <div style={ui.errBox}>⚠️ {error}</div>}
            <div style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
              {F('شماره نامه', 'letter_no')}
              {F('تاریخ نامه', 'letter_date')}
            </div>
            <div style={{display:'flex', gap:'12px', flexWrap:'wrap', marginTop:'10px'}}>
              {F('دریافتی نقدی', 'receive_cash', 'number')}
              {F('اسناد خزانه', 'receive_doc', 'number')}
              {F('تنخواه', 'receive_fund', 'number')}
            </div>
            <div style={{marginTop:'10px'}}>
              {F('توضیح', 'remark')}
            </div>
            <div style={{display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'14px'}}>
              <button onClick={()=>{ setMode(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳...' : '💾 ذخیره'}</button>
            </div>
          </div>
        )}

        {/* تایید حذف */}
        {mode === 'delete' && (
          <div style={{borderTop:'2px solid #b71c1c', padding:'18px', background:'#fff3f3', textAlign:'center'}}>
            <div style={{fontSize:'14px', fontWeight:'700', color:'#b71c1c', marginBottom:'8px'}}>آیا از حذف این دریافتی مطمئن هستید؟</div>
            <div style={{fontSize:'13px', color:'#333', marginBottom:'14px'}}>شماره نامه: {selected?.letter_no} — تاریخ: {selected?.letter_date}</div>
            {error && <div style={ui.errBox}>⚠️ {error}</div>}
            <div style={{display:'flex', justifyContent:'center', gap:'10px'}}>
              <button onClick={()=>setMode(null)} style={ui.btnCancel}>خیر</button>
              <button onClick={doDelete} disabled={saving} style={{...ui.btnSave, background:'#b71c1c'}}>{saving ? '⏳...' : 'بله، حذف شود'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const th = { ...({padding:'8px 6px', background:'#1a2f50', color:'#fff', fontSize:'12px', fontWeight:'600', textAlign:'center', borderBottom:'2px solid #0d1b2a'}) };
const td = { padding:'7px 6px', fontSize:'12px', borderBottom:'1px solid #e8eaf6' };
const numTd = { direction:'ltr', textAlign:'left', fontWeight:'600', fontFamily:"'Vazirmatn',Tahoma,sans-serif" };
